import {
  documentAuditEventsApi,
  documentExtractionSessionsApi,
  documentsApi,
} from 'app/services/api';
import _ from 'lodash';
import { normalize, schema } from 'normalizr';

import { DOCUMENT_EXTRACTION_ACTION } from 'constants/documentExtraction';
import { catchAsyncActionError } from 'utils/action';

import { getUsersAction } from 'store/users/actions';
import { selectExtendedUsers } from 'store/users/selectors';

import { getDocumentExtractionSessionId } from '../selectors';

export const GET_DOCUMENT_HISTORY = 'GET_DOCUMENT_HISTORY';
const EDIT_DOCUMENT_EXTRACTION = 'EDIT_DOCUMENT_EXTRACTION';
export const GET_ASSIGNED_DOCUMENT = 'GET_ASSIGNED_DOCUMENT';
export const GET_DOCUMENT_EXTRACTION_REQUEST = 'GET_DOCUMENT_EXTRACTION_REQUEST';
export const GET_DOCUMENT_EXTRACTION_FILLED_FIELD_IDS = 'GET_DOCUMENT_EXTRACTION_FILLED_FIELDS_IDS';
export const GET_DOCUMENT_EXTRACTION_FILLED_FIELDS = 'GET_DOCUMENT_EXTRACTION_FILLED_FIELDS';
const SKIP_DOCUMENT_BY_ID = 'SKIP_DOCUMENT_BY_ID';
const REJECT_DOCUMENT_BY_ID = 'REJECT_DOCUMENT_BY_ID';
export const GET_DOCUMENT_ASSOCIATIONS = 'GET_DOCUMENT_ASSOCIATIONS';
const EDIT_DOCUMENT_ASSOCIATIONS = 'EDIT_DOCUMENT_ASSOCIATIONS';
const RERUN_DOCUMENT_EXTRACTION = 'RERUN_DOCUMENT_EXTRACTION';

export const documentSchema = new schema.Entity(
  'data',
  {},
  {
    idAttribute: 'id',
    processStrategy: ({ typeId, lifeTimeInfo, metadata, ...restData }) => {
      return {
        ...metadata,
        companyId: metadata.CompanyId,
        loanId: metadata.LoanId,
        documentTypeId: typeId,
        createdDate: lifeTimeInfo.createdDate,
        createdByUserId: lifeTimeInfo.createdByUserId,
        lastModified: lifeTimeInfo.modifiedDate,
        status: metadata.ProcessingStatus,
        ...restData,
      };
    },
  },
);

const eventsSchema = new schema.Entity(
  'data',
  {},
  {
    idAttribute: 'id',
    processStrategy: ({ type, createdDate, description, ...event }) => ({
      ...event,
      eventType: type,
      eventDate: createdDate,
      ...(description && { rejectReason: description }),
    }),
  },
);

export const getDocumentHistoryAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const initialUsers = selectExtendedUsers(getState());

    const [{ data: documentHistory }, usersResponse] = await Promise.all([
      documentAuditEventsApi.getDocumentAuditEvents(params),
      _.isEmpty(initialUsers) ? dispatch(getUsersAction()) : null,
    ]);

    const users = usersResponse ? selectExtendedUsers(getState()) : initialUsers;

    const normalizedDocumentHistory = normalize(documentHistory, [eventsSchema]).entities.data;

    return dispatch({
      type: GET_DOCUMENT_HISTORY,
      payload: _.mapValues(normalizedDocumentHistory, value => ({
        ...value,
        userName: users[value.userId]?.name,
      })),
    });
  },
);

export const getDocumentExtractionRequestAction = catchAsyncActionError(() => async dispatch => {
  const { data } = await documentExtractionSessionsApi.getDocumentExtractionRequests();

  const getNormalizedDocument = document => {
    if (!document) {
      return null;
    }

    const normalizedData = normalize(document, documentSchema);

    return normalizedData.entities.data[normalizedData.result];
  };

  return dispatch({
    type: GET_DOCUMENT_EXTRACTION_REQUEST,
    payload: { ...data, document: getNormalizedDocument(data.document) },
  });
});

export const getDocumentExtractionFilledFieldsAction = catchAsyncActionError(
  params => async dispatch => {
    const { data } = await documentsApi.getExtractionFilledFields(params);

    dispatch({
      type: GET_DOCUMENT_EXTRACTION_FILLED_FIELDS,
      payload: data,
      meta: { documentId: params.documentId },
    });

    return data;
  },
);

export const getDocumentAssociationsAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await documentsApi.getDocumentAssociations(params);

  dispatch({
    type: GET_DOCUMENT_ASSOCIATIONS,
    payload: data,
  });

  return data;
});

export const editDocumentAssociationsAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await documentsApi.editDocumentAssociations(params);

  return dispatch({
    type: EDIT_DOCUMENT_ASSOCIATIONS,
    payload: data,
  });
});

export const rerunDocumentExtractionAction = catchAsyncActionError(params => async dispatch => {
  await documentsApi.rerunExtraction(params);

  return dispatch({ type: RERUN_DOCUMENT_EXTRACTION });
});

export const skipDocumentById = catchAsyncActionError(params => async (dispatch, getState) => {
  const sessionId = getDocumentExtractionSessionId(getState());

  const { data } = await documentExtractionSessionsApi.updateDocumentExtractionRequest({
    ...params,
    sessionId,
    type: DOCUMENT_EXTRACTION_ACTION.SKIP,
  });

  return dispatch({
    type: SKIP_DOCUMENT_BY_ID,
    payload: data,
  });
});

export const rejectDocumentById = catchAsyncActionError(params => async (dispatch, getState) => {
  const sessionId = getDocumentExtractionSessionId(getState());

  const { data } = await documentExtractionSessionsApi.updateDocumentExtractionRequest({
    ...params,
    sessionId,
    type: DOCUMENT_EXTRACTION_ACTION.REJECT,
  });

  return dispatch({
    type: REJECT_DOCUMENT_BY_ID,
    payload: data,
  });
});

export const completeDocumentExtractionById = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const sessionId = getDocumentExtractionSessionId(getState());

    const { data } = await documentExtractionSessionsApi.updateDocumentExtractionRequest({
      ...params,
      sessionId,
      type: DOCUMENT_EXTRACTION_ACTION.COMPLETE,
    });

    return dispatch({
      type: EDIT_DOCUMENT_EXTRACTION,
      payload: data,
    });
  },
);

export const overrideDocumentExtractionById = catchAsyncActionError(params => async dispatch => {
  const { data } = await documentExtractionSessionsApi.overrideDocumentExtractionRequest(params);

  return dispatch({
    type: EDIT_DOCUMENT_EXTRACTION,
    payload: data,
  });
});
